import { env } from "~/env";
import { BOOKING_FLOW_EVENTS } from "~/services/booking-flows/constants";
import { useBookingFlowEvents } from "~/services/booking-flows/hooks/use-booking-flow-events";
import { useInsurancesListQuery } from "~/services/insurances/actions";
import type { Insurance } from "~/services/insurances/types";
import { VISIT_TYPES } from "~/services/scheduling/constants";
import { INSURANCE_NOT_FOUND_OPTION } from "~/shared.types";
import { customIcons } from "~/ui";
import { StaffBookingFooter } from "../components/staff-booking-footer";
import { StepHeading } from "../components/step-heading";

const getCoachPricingCopy = (insurance: Insurance | null) => {
  if (insurance?.covers_coaching) {
    return {
      title: "Coaching is fully covered by the patient's insurance",
      description: `With ${insurance.name}, the patient won't pay anything for coaching with Elektra.`,
    };
  }

  return {
    title: "Price of the visit",
    description: insurance
      ? "Coaching with Elektra isn't covered by the patient's insurance, but it's still available."
      : "The patient will get personalized, expert coaching support.",
  };
};

type CoachPricingStepProps = {
  stateId: number;
  insuranceId: number | null;
  onContinue: () => void;
  onBack: () => void;
};

export const CoachPricingStep = ({
  stateId,
  insuranceId,
  onContinue,
  onBack,
}: CoachPricingStepProps) => {
  const { trackTimeEvent } = useBookingFlowEvents();
  const hasPlan =
    insuranceId !== null && insuranceId !== INSURANCE_NOT_FOUND_OPTION.value;

  const { data: insurances, isPending } = useInsurancesListQuery({
    stateId: hasPlan ? stateId : null,
  });
  const insurance = hasPlan
    ? (insurances?.find((option) => option.id === insuranceId) ?? null)
    : null;
  const isLoading = hasPlan && isPending;

  const sessionCost = insurance?.covers_coaching
    ? 0
    : env.VITE_COACH_VISIT_COST;
  const { title, description } = getCoachPricingCopy(insurance);

  const handleContinue = () => {
    trackTimeEvent(BOOKING_FLOW_EVENTS.INSURANCE_PAGE_SUBMITTED_AT);
    onContinue();
  };

  return (
    <div className="flex min-h-full flex-col gap-6">
      <StepHeading title="Book a visit" visitType={VISIT_TYPES.COACH} />

      {isLoading ? (
        <div role="status" className="flex justify-center py-10">
          <customIcons.SpinnerIcon
            aria-hidden="true"
            className="text-violet size-10 animate-spin"
          />
          <span className="sr-only">Loading price</span>
        </div>
      ) : (
        <section className="flex flex-col gap-4">
          <h3 className="font-ivy text-indigo text-xl font-normal">{title}</h3>
          <div className="flex flex-col gap-1 rounded-lg bg-indigo-50 p-8 text-center">
            <p className="font-ivy text-2xl font-bold">${sessionCost}</p>
            <p>per Coach visit</p>
          </div>
          <p className="text-base text-neutral-700">{description}</p>
        </section>
      )}

      <StaffBookingFooter
        onBack={onBack}
        continueProps={{
          type: "button",
          onClick: handleContinue,
          disabled: isLoading,
        }}
      />
    </div>
  );
};
